import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaChartLine as Activity,
  FaBrain as Brain,
  FaSearch as Search,
  FaBullseye as Target,
  FaUsers as Users
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  LineChart,
  Line,
  CartesianGrid
} from 'recharts';
import './styles.css';

import env from './env.json';
const API = env.BACKEND_URL;

function Stat({ label, value, suffix = '', sub }) {
  return (
      <div className="stat">
        <span>{label}</span>
        <b>{value}{suffix}</b>
        {sub && <small>{sub}</small>}
      </div>
  );
}

function Pill({ children, tone = 'neutral' }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

function formatDelta(value) {
  if (value > 0) return `+${value}`;
  return value;
}

function App() {
  const [lineups, setLineups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [question, setQuestion] = useState('Summarize the strongest signal, main caveat, and best use case for this lineup.');
  const [insight, setInsight] = useState('');
  const [aiMode, setAiMode] = useState('');
  const [loadingInsight, setLoadingInsight] = useState(false);
  const [filter, setFilter] = useState('flow');

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/lineups`).then((res) => res.json()),
      fetch(`${API}/api/summary`).then((res) => res.json())
    ]).then(([lineupData, summaryData]) => {
      setLineups(lineupData);
      setSummary(summaryData);
      setSelectedId(lineupData[0]?.id || '');
    });
  }, []);

  const selected = useMemo(
      () => lineups.find((lineup) => lineup.id === selectedId) || lineups[0],
      [lineups, selectedId]
  );

  const filteredLineups = useMemo(() => {
    const sorters = {
      flow: (a, b) => b.flowScore - a.flowScore,
      movement: (a, b) => b.movementIndex - a.movementIndex,
      spacing: (a, b) => b.spacingIndex - a.spacingIndex,
      pressure: (a, b) => b.pressureIndex - a.pressureIndex,
      sample: (a, b) => a.minutes - b.minutes
    };

    return [...lineups].sort(sorters[filter] || sorters.flow);
  }, [lineups, filter]);

  useEffect(() => {
    if (!selected?.id) return;
    getInsight(selected.id, question);
    fetch(`${API}/api/lineups/${selected.id}/drivers`).then((res) => res.json()).then(setDrivers);
  }, [selected?.id]);

  async function getInsight(lineupId = selectedId, q = question) {
    setLoadingInsight(true);
    try {
      const res = await fetch(`${API}/api/insight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lineupId, question: q })
      });
      const data = await res.json();
      setInsight(data.insight);
      setAiMode(data.mode);
    } finally {
      setLoadingInsight(false);
    }
  }

  if (!selected) return <main className="page loading">Loading lineups…</main>;

  const radarData = [
    { metric: 'Movement', value: selected.movementIndex },
    { metric: 'Spacing', value: selected.spacingIndex },
    { metric: 'Pressure', value: selected.pressureIndex },
    { metric: 'Shot quality', value: selected.expectedEfg },
    { metric: 'Control', value: 100 - selected.turnoverRate },
    { metric: 'Net', value: selected.netRating + 50 }
  ];

  const trendData = selected.trend.map((value, index) => ({ game: `G${index + 1}`, offRating: value }));
  const topLineup = lineups[0];
  const modeLabel = 'Live AI';

  return (
      <main className="page">
        <header className="topbar">
          <div>
            <p className="kicker">Lineup analysis</p>
            <h1>Warriors Flow</h1>
            <p className="subhead">A compact tool for comparing lineup process signals and generating grounded analyst notes.</p>
          </div>
          <div className="top-stats">
            <Stat label="Lineups" value={lineups.length} />
            <Stat label="Top Flow" value={topLineup?.flowScore ?? '—'} sub={topLineup?.archetype} />
            <Stat label="Data Type" value='Sample' />
          </div>
        </header>

        {summary && (
            <section className="panel summary-panel">
              <div className="section-title">Summary</div>
              <p className="summary-line">{summary.headline}</p>
              <div className="summary-list">
                {summary.bullets.map((bullet) => <p key={bullet}>{bullet}</p>)}
              </div>
              <p className="caveat">{summary.caveat}</p>
            </section>
        )}

        <section className="layout">
          <aside className="panel board-panel">
            <div className="panel-head">
              <div>
                <div className="section-title"><Users size={16} /> Lineups</div>
                <p>Sort by the signal you want to inspect.</p>
              </div>
            </div>

            <div className="filters" aria-label="Sort lineups">
              {[
                ['flow', 'Flow'],
                ['movement', 'Movement'],
                ['spacing', 'Spacing'],
                ['pressure', 'Pressure'],
                ['sample', 'Small samples']
              ].map(([id, label]) => (
                  <button key={id} className={filter === id ? 'chip active' : 'chip'} onClick={() => setFilter(id)}>{label}</button>
              ))}
            </div>

            <div className="lineup-list">
              {filteredLineups.map((lineup) => {
                const score = filter === 'movement' ? lineup.movementIndex :
                    filter === 'spacing' ? lineup.spacingIndex :
                        filter === 'pressure' ? lineup.pressureIndex :
                            filter === 'sample' ? lineup.minutes : lineup.flowScore;

                return (
                    <button
                        key={lineup.id}
                        className={lineup.id === selectedId ? 'lineup-row selected' : 'lineup-row'}
                        onClick={() => setSelectedId(lineup.id)}
                    >
                  <span>
                    <b>{lineup.archetype}</b>
                    <small>{lineup.lineup.join(' · ')}</small>
                  </span>
                      <strong>{score}</strong>
                    </button>
                );
              })}
            </div>
          </aside>

          <section className="main-stack">
            <section className="panel selected-panel">
              <div className="selected-top">
                <div>
                  <div className="section-title"><Activity size={16} /> Selected lineup</div>
                  <h2>{selected.archetype}</h2>
                  <p>{selected.lineup.join(' · ')}</p>
                </div>
                <div className="selected-tags">
                  <Pill tone={selected.trendArrow === 'rising' ? 'positive' : 'neutral'}>{selected.trendArrow} trend</Pill>
                  <Pill>{Math.round(selected.sampleConfidence * 100)}% sample confidence</Pill>
                </div>
              </div>

              <div className="stats-grid">
                <Stat label="Flow Score" value={selected.flowScore} sub="Composite score" />
                <Stat label="Net Rating" value={formatDelta(selected.netRating)} />
                <Stat label="Expected eFG" value={selected.expectedEfg} suffix="%" />
                <Stat label="Shot delta" value={formatDelta(selected.shotMakingDelta)} sub="eFG - x eFG" />
                <Stat label="Movement" value={selected.movementIndex} />
                <Stat label="Spacing" value={selected.spacingIndex} />
                <Stat label="Pressure" value={selected.pressureIndex} />
                <Stat label="Minutes" value={selected.minutes} />
              </div>
            </section>

            <section className="grid two">
              <div className="panel ai-panel">
                <div className="panel-head compact">
                  <div className="section-title"><Brain size={16} /> Analyst note</div>
                  <Pill>{modeLabel}</Pill>
                </div>
                <textarea value={question} onChange={(e) => setQuestion(e.target.value)} />
                <button className="primary" onClick={() => getInsight()} disabled={loadingInsight}>
                  <Search size={16} /> {loadingInsight ? 'Generating…' : 'Generate note'}
                </button>
                <div className="insight">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {insight}
                  </ReactMarkdown>
                </div>
              </div>

              <div className="panel driver-panel">
                <div className="section-title"><Target size={16} /> Drivers</div>
                {drivers.map((driver, index) => (
                    <div className="driver" key={driver.label}>
                      <div>
                        <b>{index + 1}. {driver.label}</b>
                        <p>{driver.evidence}</p>
                      </div>
                      <strong>{Math.round(driver.value)}</strong>
                    </div>
                ))}
              </div>
            </section>

            <section className="grid charts">
              <div className="panel chart-panel">
                <div className="section-title">Recent offensive rating</div>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="game" />
                    <YAxis domain={[85, 135]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="offRating" strokeWidth={3} dot />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="panel chart-panel">
                <div className="section-title">Style profile</div>
                <ResponsiveContainer width="100%" height={260}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" />
                    <Radar dataKey="value" fillOpacity={0.25} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="panel chart-panel">
              <div className="section-title">Flow score distribution</div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={lineups.map((lineup, index) => ({ name: `L${index + 1}`, score: lineup.flowScore }))}>
                  <XAxis dataKey="name" />
                  <YAxis domain={[60, 115]} />
                  <Tooltip />
                  <Bar dataKey="score" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </section>
          </section>
        </section>
      </main>
  );
}

export default App;